// Lifecycle windows + a point on the axis → per-node paint.
//
// ONE resolver, shared by both backends. It lived twice — once in the headless
// renderer and once in the app-shell hook — with a comment on each saying it
// deliberately mirrored the other because the headless path must not depend on
// React. But nothing here is React: it is a pure function of a window map and
// an ordinal, and the hook is just a `useMemo` around it. The two copies had
// already drifted in how they spell the badge.
//
// That drift is the whole failure mode. `edgeLifecycle` documents the same
// lesson for edges and fixed it by folding the geometry into `@archlang/scene`;
// nodes get the same treatment here, one layer down, because this needs the
// engine's `lifecycleStateAt` and scene must stay engine-free.
//
// A point is an ORDINAL, never an instant — an index into the model's own
// sorted marker list, so both backends are asking the same question of the same
// axis and cannot disagree about what is planned or deprecated at a given spot.

import {
  EMPTY_TIMELINE,
  labelAt,
  lifecycleStateAt,
  type LifecycleState,
  type LifecycleWindow,
  type Timeline,
} from "@archlang/engine";
import type { LifecyclePaint, LifecycleState as SceneLifecycleState } from "@archlang/scene/board";

// scene must stay engine-free, so it declares its OWN copy of the state union
// and says it "mirrors the engine's". Nothing enforced that, and a mirror with
// no glass is just a second source of truth — the engine could gain a state and
// scene would keep painting four.
//
// This module is the one place that imports both, so the parity check belongs
// here. Compile-time and zero runtime: if the two unions stop being mutually
// assignable, `true` is no longer assignable to `false` and the build fails
// naming this line.
type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const _lifecycleStatesAgree: Exact<LifecycleState, SceneLifecycleState> = true;
void _lifecycleStatesAgree;

/** Resolved paint for every node that carries a lifecycle window. */
export type LifecyclePaintMap = ReadonlyMap<string, LifecyclePaint>;

/**
 * Paint for every windowed node at ordinal `t`.
 *
 * A node reading plain `live` is omitted: paint is an override, and an absent
 * entry means "draw it normally". The one exception is a sunset that is
 * announced but not yet open — still live, but worth a badge, and the only
 * lifecycle signal that does not depend on where the scrubber sits.
 */
export function resolveLifecyclePaint(
  windows: ReadonlyMap<string, LifecycleWindow>,
  t: number,
  timeline: Timeline | undefined,
): LifecyclePaintMap {
  const out = new Map<string, LifecyclePaint>();
  if (windows.size === 0) return out;
  const tl = timeline ?? EMPTY_TIMELINE;
  for (const [key, w] of windows) {
    const state: LifecycleState = lifecycleStateAt(w, t);
    const sunsetScheduled = state === "live" && w.deadFrom !== undefined && t < w.deadFrom;
    if (state === "live" && !sunsetScheduled) continue;
    // The badge echoes the author's OWN marker — never a date invented for it.
    const sunsetAt = sunsetScheduled ? labelAt(tl, w.deadFrom!) : undefined;
    const badge = state === "retired"
      ? "retired"
      : sunsetScheduled
        ? sunsetAt
          ? `sunset ${sunsetAt}`
          : "sunset"
        : undefined;
    out.set(key, { state, sunsetScheduled, badge });
  }
  return out;
}

/**
 * The delta between two points, classified for diff paint. Both ends read the
 * SAME committed text, so nothing is added or removed — only born, retired, or
 * entering deprecation. That is why this needs no model diff.
 */
export type LifecycleDelta = ReadonlyMap<string, "born" | "retired" | "deprecating">;

export function resolveLifecycleDeltaAt(
  windows: ReadonlyMap<string, LifecycleWindow>,
  a0: number,
  b0: number,
): LifecycleDelta {
  const out = new Map<string, "born" | "retired" | "deprecating">();
  for (const [key, w] of windows) {
    const a = lifecycleStateAt(w, a0);
    const b = lifecycleStateAt(w, b0);
    if (a === b) continue;
    if (b === "retired") out.set(key, "retired");
    else if (b === "deprecated") out.set(key, "deprecating");
    else if (a === "planned") out.set(key, "born");
  }
  return out;
}
